import React, { useState, useEffect } from 'react';
import { colors, radius, fonts } from '../../theme';
import { latestScan, clientJoinedAt } from '../../services/bodyScanService';
import { scanReminder, readReminderSnooze, afterNotNow } from '../../utils/bodyStats';
import { Button, Dialog } from './parts';
import PhotoScan from './PhotoScan';

// The four-weekly reminder on the client's Workout screen (owner, 17 October 2026): no scan yet, or the
// latest four weeks old or more. Owner, 18 October 2026: one slim line below the week's workouts, not in
// a new client's first week; "Not now" hides it for a week on this device, and the third time running
// for four weeks. "Add scan" opens the same steps as Body stats. Says nothing if the scans cannot be read.
const snoozeKey = (clientId) => `cipher_scan_reminder_snooze:${clientId}`;
const readSnooze = (clientId) => { try { return readReminderSnooze(localStorage.getItem(snoozeKey(clientId))); } catch { return readReminderSnooze(null); } };

export default function ScanReminder({ clientId }) {
  const [due, setDue] = useState(null);
  const [lastMachine, setLastMachine] = useState(null);
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(0); // bumped after a save, to look again

  // Worked out from the latest scan each time: on opening, for another client, and after any save (an
  // older scan added from history leaves the reminder up). A new client starts with nothing shown.
  useEffect(() => {
    setDue(null);
    setOpen(false);
    if (!clientId) return undefined;
    let gone = false;
    Promise.all([latestScan(clientId), clientJoinedAt(clientId).catch(() => null)])
      .then(([latest, joinedAt]) => {
        if (gone) return;
        setLastMachine(latest?.machine ?? null);
        setDue(scanReminder({ latestDay: latest?.measured_on ?? null, joinedAt, snoozedUntil: readSnooze(clientId).until }));
      })
      .catch(() => {});
    return () => { gone = true; };
  }, [clientId, checked]);

  if (!due) return null;
  const notNow = () => {
    try { localStorage.setItem(snoozeKey(clientId), JSON.stringify(afterNotNow(readSnooze(clientId)))); } catch { /* storage blocked */ }
    setDue(null);
  };
  const saved = () => {
    try { localStorage.removeItem(snoozeKey(clientId)); } catch { /* storage blocked */ }
    setOpen(false);
    setChecked((n) => n + 1);
  };
  const weeks = due.days ? Math.floor(due.days / 7) : null;

  return (
    <div role="region" aria-label="Body scan reminder" style={{
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 12px',
      background: colors.surface1, border: `1px solid ${colors.border}`, borderRadius: radius.sm,
      padding: '10px 12px', margin: '12px 0', fontFamily: fonts.body, color: colors.text,
    }}>
      <div style={{ flex: '1 1 180px', minWidth: 0, fontSize: 13, lineHeight: 1.4 }}>
        <span style={{ fontWeight: 700 }}>{due.neverScanned ? 'Add a body scan' : 'Body scan due'}</span>
        <span style={{ color: colors.textMuted }}>
          {due.neverScanned
            ? ': a photo of your gym’s sheet or a screenshot from your scale’s app.'
            : `: your last was ${weeks} weeks ago.`}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
        <Button kind="primary" onClick={() => setOpen(true)} style={{ minHeight: 44, fontSize: 14, padding: '0 14px' }}>Add scan</Button>
        <Button kind="quiet" onClick={notNow} style={{ minHeight: 44, fontSize: 14, padding: '0 10px' }}>Not now</Button>
      </div>
      {open && (
        <Dialog title="Add scan" onClose={() => setOpen(false)}>
          <PhotoScan clientId={clientId} lastMachine={lastMachine} onSaved={saved} onCancel={() => setOpen(false)} />
        </Dialog>
      )}
    </div>
  );
}
